import { getSportPositionsForSport } from "@/utils/sport-positions";

export type SportCourtStyle =
  | "football"
  | "volleyball-indoor"
  | "volleyball-sand"
  | "beach-tennis"
  | "carimba";

export type SportPositionMarker = {
  value: string;
  label: string;
  shortLabel: string;
  x: number;
  y: number;
};

export type SportFieldLayout = {
  aspectRatio: string;
  courtStyle: SportCourtStyle;
  markers: Record<string, { x: number; y: number; shortLabel?: string }>;
};

function layoutKey(sport: string, sportCategory?: string | null): string | null {
  if (sport === "FUTEBOL") return "FUTEBOL";
  if (sport === "BEACH_TENNIS") return "BEACH_TENNIS";
  if (sport === "CARIMBA") return "CARIMBA";
  if (sport === "FUTEVOLEI") return "FUTEVOLEI";
  if (sport === "VOLEI") {
    if (sportCategory === "VOL_SAND") return "VOLEI_SAND";
    if (sportCategory === "VOL_INDOOR") return "VOLEI_INDOOR";
    const positions = getSportPositionsForSport(sport, sportCategory);
    if (positions.some((p) => p.value === "PONTEIRO")) return "VOLEI_SAND";
    return "VOLEI_INDOOR";
  }
  return null;
}

const FIELD_LAYOUTS: Record<string, SportFieldLayout> = {
  FUTEBOL: {
    aspectRatio: "3/4",
    courtStyle: "football",
    markers: {
      GOLEIRO: { x: 50, y: 92, shortLabel: "GOL" },
      ZAGUEIRO: { x: 50, y: 74, shortLabel: "ZAG" },
      LATERAL: { x: 16, y: 58, shortLabel: "LAT" },
      VOLANTE: { x: 50, y: 52, shortLabel: "VOL" },
      MEIA: { x: 50, y: 36, shortLabel: "MEI" },
      ATACANTE: { x: 50, y: 14, shortLabel: "ATA" },
    },
  },
  VOLEI_INDOOR: {
    aspectRatio: "3/4",
    courtStyle: "volleyball-indoor",
    markers: {
      PONTA: { x: 18, y: 58, shortLabel: "PNT" },
      CENTRAL: { x: 50, y: 54, shortLabel: "CEN" },
      OPOSTO: { x: 82, y: 58, shortLabel: "OPO" },
      LEVANTADOR: { x: 82, y: 82, shortLabel: "LEV" },
      LIBERO: { x: 50, y: 86, shortLabel: "LÍB" },
    },
  },
  VOLEI_SAND: {
    aspectRatio: "3/4",
    courtStyle: "volleyball-sand",
    markers: {
      LEVANTADOR: { x: 50, y: 52, shortLabel: "LEV" },
      PONTEIRO: { x: 24, y: 68, shortLabel: "PTE" },
      OPOSTO: { x: 76, y: 68, shortLabel: "OPO" },
      CENTRAL: { x: 50, y: 84, shortLabel: "CEN" },
    },
  },
  BEACH_TENNIS: {
    aspectRatio: "4/5",
    courtStyle: "beach-tennis",
    markers: {
      ESQUERDA: { x: 28, y: 50, shortLabel: "ESQ" },
      DIREITA: { x: 72, y: 50, shortLabel: "DIR" },
    },
  },
  FUTEVOLEI: {
    aspectRatio: "4/5",
    courtStyle: "volleyball-sand",
    markers: {
      FRENTE_ESQ: { x: 28, y: 22, shortLabel: "Frente esq." },
      FRENTE_DIR: { x: 72, y: 22, shortLabel: "Frente dir." },
      FUNDO_ESQ: { x: 28, y: 78, shortLabel: "Fundo esq." },
      FUNDO_DIR: { x: 72, y: 78, shortLabel: "Fundo dir." },
    },
  },
  CARIMBA: {
    aspectRatio: "3/4",
    courtStyle: "carimba",
    markers: {
      ATAQUE: { x: 50, y: 24, shortLabel: "ATQ" },
      DEFESA: { x: 50, y: 76, shortLabel: "DEF" },
    },
  },
};

export function getSportFieldLayout(
  sport: string | undefined | null,
  sportCategory?: string | null,
): SportFieldLayout | null {
  if (!sport) return null;
  const key = layoutKey(sport, sportCategory);
  if (!key) return null;
  return FIELD_LAYOUTS[key] ?? null;
}

export function getSportPositionMarkers(
  sport: string | undefined | null,
  sportCategory?: string | null,
): SportPositionMarker[] | null {
  const layout = getSportFieldLayout(sport, sportCategory);
  const positions = getSportPositionsForSport(sport, sportCategory);
  if (!layout || positions.length === 0) return null;

  return positions
    .map((position) => {
      const marker = layout.markers[position.value];
      if (!marker) return null;
      return {
        value: position.value,
        label: position.label,
        shortLabel: marker.shortLabel ?? position.label.slice(0, 3).toUpperCase(),
        x: marker.x,
        y: marker.y,
      };
    })
    .filter((item): item is SportPositionMarker => item !== null);
}

export function sportHasPositionLayout(
  sport: string | undefined | null,
  sportCategory?: string | null,
): boolean {
  return getSportPositionMarkers(sport, sportCategory) !== null;
}
